import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { FiAward, FiBookOpen, FiBriefcase, FiFileText } from "react-icons/fi";

export const Gallery = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  };

  const educationItems = [
    "Aile Danışmanlığı – Gökhan Çınar",
    "Diksiyon – Ece Üner & Erdoğan Arıkan",
    "DIRFloortime – Egemen Nakışçı",
    "Oyun Terapisi & Eğiticinin Eğitimi – Tecrübe Akademi",
  ];

  const experienceItems = [
    "İstanbul Avcılar Rehberlik ve Araştırma Merkezi (RAM)",
    "İstanbul Eğitim ve Araştırma Hastanesi",
    "Kadıköy Moda Özel Anaokulu",
    "Rehabilitasyon Merkezi",
    "Gönüllü alan çalışmaları ve klinik deneyimler",
  ];

  // Schema.org JSON-LD for Person (Dilan Aslan)
  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    mainEntity: {
      "@type": "Person",
      name: "Dilan Aslan",
      jobTitle: "Çocuk Gelişim Uzmanı & MEB Onaylı Aile Danışmanı",
      description:
        "İstanbul Gelişim Üniversitesi Çocuk Gelişimi mezunu, MEB onaylı aile danışmanı. Mardin Artuklu'da otizm, DEHB, dil ve öğrenme güçlükleri üzerine profesyonel danışmanlık hizmeti sunmaktadır.",
      image: "https://www.dilanaslan.com/img/hakkimda.jpg",
      url: "https://www.dilanaslan.com",
      telephone: "+905055517747",
      email: "info@dilanaslan.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Artuklu",
        addressLocality: "Mardin",
        addressRegion: "Mardin",
        postalCode: "47200",
        addressCountry: "TR",
      },
      alumniOf: {
        "@type": "CollegeOrUniversity",
        name: "İstanbul Gelişim Üniversitesi",
        department: "Sağlık Fakültesi - Çocuk Gelişimi Bölümü",
      },
      hasCredential: [
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "Lisans Diploması",
          educationalLevel: "Lisans",
          recognizedBy: {
            "@type": "Organization",
            name: "İstanbul Gelişim Üniversitesi",
          },
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "Sertifika",
          name: "MEB Onaylı Aile Danışmanlığı Sertifikası",
          recognizedBy: {
            "@type": "Organization",
            name: "Milli Eğitim Bakanlığı",
          },
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "Sertifika",
          name: "DIRFloortime Terapisti Sertifikası",
        },
        {
          "@type": "EducationalOccupationalCredential",
          credentialCategory: "Sertifika",
          name: "Oyun Terapisi Eğitici Eğitimi Sertifikası",
        },
      ],
      knowsAbout: [
        "Çocuk Gelişimi",
        "Otizm Spektrum Bozukluğu",
        "DEHB (Dikkat Eksikliği Hiperaktivite Bozukluğu)",
        "Dil ve İletişim Güçlükleri",
        "Öğrenme Güçlükleri",
        "Oyun Terapisi",
        "DIRFloortime Terapi",
        "Aile Danışmanlığı",
      ],
      worksFor: {
        "@type": "ProfessionalService",
        name: "Dilan Aslan Çocuk Gelişim Danışmanlığı",
        url: "https://www.dilanaslan.com",
      },
      sameAs: ["https://www.instagram.com/cg.dilanaslan/"],
    },
  };

  return (
    <section
      id="portfolio"
      style={{
        padding: "96px 0",
        background: "linear-gradient(to bottom, #f9fafb, white)",
      }}
    >
      <Helmet>
        {/* Temel Meta Etiketleri */}
        <html lang="tr" />
        <title>
          Dilan Aslan Kimdir? | Çocuk Gelişim Uzmanı - Mardin Artuklu
        </title>
        <meta
          name="description"
          content="Dilan Aslan, İstanbul Gelişim Üniversitesi Çocuk Gelişimi mezunu, MEB onaylı aile danışmanı. Mardin Artuklu'da otizm, DEHB, dil ve öğrenme güçlükleri, oyun terapisi ve DIRFloortime konularında 0-18 yaş çocuklara profesyonel danışmanlık."
        />
        <meta
          name="keywords"
          content="Dilan Aslan, çocuk gelişim uzmanı Mardin, MEB onaylı aile danışmanı, otizm uzmanı Artuklu, DEHB danışmanı, öğrenme güçlüğü uzmanı, oyun terapisti Mardin, DIRFloortime terapisti, çocuk danışmanı Mardin, İstanbul Gelişim Üniversitesi mezunu"
        />
        <meta name="author" content="Dilan Aslan" />
        <link rel="canonical" href="https://www.dilanaslan.com/hakkimda" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content="https://www.dilanaslan.com/hakkimda" />
        <meta
          property="og:title"
          content="Dilan Aslan | Çocuk Gelişim Uzmanı & MEB Onaylı Aile Danışmanı"
        />
        <meta
          property="og:description"
          content="İstanbul Gelişim Üniversitesi mezunu, MEB onaylı aile danışmanı. Mardin Artuklu'da otizm, DEHB, dil ve öğrenme güçlükleri üzerine profesyonel danışmanlık."
        />
        <meta
          property="og:image"
          content="https://www.dilanaslan.com/img/hakkimda.jpg"
        />
        <meta property="og:locale" content="tr_TR" />
        <meta
          property="og:site_name"
          content="Dilan Aslan Çocuk Gelişim Danışmanlığı"
        />
        <meta property="profile:first_name" content="Dilan" />
        <meta property="profile:last_name" content="Aslan" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:url"
          content="https://www.dilanaslan.com/hakkimda"
        />
        <meta
          name="twitter:title"
          content="Dilan Aslan | Çocuk Gelişim Uzmanı - Mardin Artuklu"
        />
        <meta
          name="twitter:description"
          content="MEB onaylı aile danışmanı. Otizm, DEHB, dil ve öğrenme güçlükleri üzerine profesyonel danışmanlık."
        />
        <meta
          name="twitter:image"
          content="https://www.dilanaslan.com/img/hakkimda.jpg"
        />

        {/* Coğrafi Etiketler */}
        <meta name="geo.region" content="TR-47" />
        <meta name="geo.placename" content="Mardin, Artuklu" />
        <meta name="geo.position" content="37.3212;40.7245" />
        <meta name="ICBM" content="37.3212, 40.7245" />

        {/* Mobil Optimizasyon */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#2563EB" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>

        {/* Robots */}
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
        <meta name="googlebot" content="index, follow" />
      </Helmet>

      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 24px",
        }}
      >
        {/* Başlık */}
        <motion.div
          style={{ textAlign: "center", marginBottom: "64px" }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#111827",
              marginBottom: "16px",
            }}
          >
            Hakkımda
          </h2>
          <p
            style={{
              fontSize: "1.25rem",
              color: "#6b7280",
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Çocuk Gelişim Uzmanı & MEB Onaylı Aile Danışmanı
          </p>
        </motion.div>

        {/* Üst Bölüm - Fotoğraf ve Tanıtım */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "48px",
            alignItems: "center",
            marginBottom: "64px",
          }}
        >
          {/* Fotoğraf */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ position: "relative" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                borderRadius: "24px",
                padding: "16px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              }}
            >
              <img
                src="/img/hakkimda.jpg"
                alt="Mardin Artuklu çocuk gelişim danışmanı Dilan Aslan"
                style={{
                  width: "100%",
                  height: "450px",
                  objectFit: "cover",
                  borderRadius: "16px",
                }}
              />
            </div>
            {/* Badge */}
            <div
              style={{
                position: "absolute",
                bottom: "-20px",
                right: "20px",
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "16px 24px",
                boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#dbeafe",
                  borderRadius: "12px",
                  padding: "12px",
                }}
              >
                <FiAward size={24} color="#2563eb" />
              </div>
              <div>
                <p
                  style={{
                    fontWeight: "700",
                    color: "#111827",
                    fontSize: "1rem",
                  }}
                >
                  MEB Onaylı
                </p>
                <p style={{ color: "#6b7280", fontSize: "0.875rem" }}>
                  Aile Danışmanı
                </p>
              </div>
            </div>
          </motion.div>

          {/* Tanıtım Metni */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ staggerChildren: 0.2 }}
          >
            <motion.div
              variants={fadeInUp}
              style={{
                display: "inline-block",
                backgroundColor: "#dbeafe",
                color: "#1d4ed8",
                padding: "8px 16px",
                borderRadius: "50px",
                fontSize: "0.875rem",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              İstanbul Gelişim Üniversitesi Mezunu
            </motion.div>

            <motion.h3
              variants={fadeInUp}
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "24px",
              }}
            >
              Merhaba, ben <span style={{ color: "#2563eb" }}>Dilan Aslan</span>
            </motion.h3>

            <motion.p
              variants={fadeInUp}
              style={{
                fontSize: "1.125rem",
                color: "#4b5563",
                lineHeight: "1.75",
                marginBottom: "16px",
              }}
            >
              İstanbul Gelişim Üniversitesi Sağlık Fakültesi Çocuk Gelişimi
              bölümünden mezun oldum. 0–18 yaş arası tüm çocuklarla çalışıyor,
              özellikle farklı gelişim özelliklerine sahip çocuklar ve
              ailelerine destek sunuyorum.
            </motion.p>

            <motion.p
              variants={fadeInUp}
              style={{
                fontSize: "1.125rem",
                color: "#4b5563",
                lineHeight: "1.75",
                marginBottom: "24px",
              }}
            >
              Çalışma alanlarım arasında <strong>Otizm</strong>,{" "}
              <strong>DEHB</strong>, <strong>dil ve iletişim güçlükleri</strong>
              , <strong>öğrenme zorlukları</strong> ve genel gelişimsel destek
              ihtiyaçları bulunmaktadır.
            </motion.p>

            {/* İletişim Butonları */}
            <motion.div
              variants={fadeInUp}
              style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}
            >
              <a
                href="#contact"
                style={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  padding: "14px 28px",
                  borderRadius: "12px",
                  fontWeight: "600",
                  textDecoration: "none",
                  display: "inline-block",
                  transition: "all 0.2s ease",
                }}
              >
                İletişime Geç
              </a>
              <a
                href="tel:+905055517747"
                style={{
                  backgroundColor: "white",
                  color: "#2563eb",
                  padding: "14px 28px",
                  borderRadius: "12px",
                  fontWeight: "600",
                  textDecoration: "none",
                  display: "inline-block",
                  border: "2px solid #2563eb",
                  transition: "all 0.2s ease",
                }}
              >
                Hemen Ara
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Alt Kartlar - Eğitim ve Deneyim */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "32px",
            marginBottom: "64px",
          }}
        >
          {/* Profesyonel Eğitimler */}
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "24px",
              padding: "32px",
              border: "2px dashed #e5e7eb",
              transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div
              style={{
                backgroundColor: "#2563eb",
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <FiBookOpen size={28} color="white" />
            </div>
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "20px",
              }}
            >
              🎓 Profesyonel Eğitimlerim
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {educationItems.map((item, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                    color: "#4b5563",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#dbeafe",
                      borderRadius: "50%",
                      minWidth: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "12px",
                      marginTop: "2px",
                      color: "#2563eb",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Staj ve Klinik Deneyimler */}
          <motion.div
            style={{
              backgroundColor: "white",
              borderRadius: "24px",
              padding: "32px",
              border: "2px dashed #e5e7eb",
              transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div
              style={{
                backgroundColor: "#2563eb",
                width: "56px",
                height: "56px",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <FiBriefcase size={28} color="white" />
            </div>
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "20px",
              }}
            >
              🏥 Staj ve Klinik Deneyimlerim
            </h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {experienceItems.map((item, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                    color: "#4b5563",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#dbeafe",
                      borderRadius: "50%",
                      minWidth: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: "12px",
                      marginTop: "2px",
                      color: "#2563eb",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Akademik Çalışma */}
        <motion.div
          style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            borderRadius: "24px",
            padding: "48px",
            color: "white",
            textAlign: "center",
          }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              width: "64px",
              height: "64px",
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <FiFileText size={32} color="white" />
          </div>
          <h3
            style={{
              fontSize: "1.75rem",
              fontWeight: "700",
              marginBottom: "16px",
            }}
          >
            📑 Akademik Çalışmam
          </h3>
          <p
            style={{
              fontSize: "1.125rem",
              lineHeight: "1.75",
              maxWidth: "800px",
              margin: "0 auto",
              opacity: 0.95,
            }}
          >
            İstanbul Gelişim Üniversitesi Sağlık Fakültesi lisans mezuniyet
            tezim,{" "}
            <strong>
              "Çocuklarda Dil Gelişimi: Çocuklarda Dil Becerilerini Geliştirmek
              için Eğitim ve Aktiviteler"
            </strong>{" "}
            üzerineydi. Yurtiçi ve yurtdışı kapsamında incelediğim makalelerle
            çocukların gelişim yolculuklarında hem bireysel hem de aile odaklı
            çalışarak, onların potansiyellerini en sağlıklı şekilde ortaya
            çıkarmayı hedefliyorum.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Gallery;
